import { useEffect, useState } from 'react';
import { Control, FieldValues, UseFormSetValue } from 'react-hook-form';

import { Icon } from 'coreOld/elements';
import { IOption } from 'coreOld/types';

import { FormatLabelStyled } from './creatableSelectStyle';
import CreatableSelectView from './CreatableSelectView';
import CreatableSelectWithControllerView from './CreatableSelectWithControllerView';

interface IProps {
  name: string;
  value?: string | number | string[] | number[];
  defaultValue?: string | number | string[] | number[];
  placeholder?: string;
  menuIsOpen?: boolean;
  customStyles?;
  control?: Control<FieldValues, object>;
  setValue?: UseFormSetValue<FieldValues>;
  isMulti?: boolean;
  mapDataToFilters: (options, onChange) => void;
  showDropdownIcon?: boolean;
  disabled?: boolean;
  editable?: boolean;
  getValue?;
  getLabel?;
  onChange?;
  options?;
  isLoadingOptions?: boolean;
  onKeyDown?;
  isClearable?: boolean;
  createOption?: (label: string) => IOption;
  onCreateOption?: (inputValue: string) => void;
  formatCreateLabel?: (inputValue: string) => JSX.Element;
}

const handleCreateOption = (label: string) => ({
  label,
  value: label.toLowerCase().replace(/\W/g, ''),
});

const handleFormatCreateLabel = (inputValue: string) => (
  <FormatLabelStyled>
    <Icon name={Icon.STRONG_PLUS} />
    {inputValue}
  </FormatLabelStyled>
);

const CreatableSelect = (props: IProps) => {
  const {
    customStyles,
    mapDataToFilters,
    options,
    onChange,
    setValue,
    getValue = (option) => option.value,
    getLabel = (option) => option.label,
    isLoadingOptions,
    createOption = handleCreateOption,
    onCreateOption,
    formatCreateLabel = handleFormatCreateLabel,
  } = props;

  const [selectOptions, setSelectOptions] = useState<IOption[] | []>(options);

  useEffect(() => {
    if (isLoadingOptions || selectOptions?.length > 0) {
      return;
    }

    if (options?.length > 0) {
      setSelectOptions((prev) => [...prev, ...options]);
    }
  }, [isLoadingOptions, options?.length]);

  const handleCreate = (inputValue: string) => {
    if (isLoadingOptions) {
      return;
    }

    if (onCreateOption) {
      onCreateOption(inputValue);

      return;
    }

    const newOption = createOption(inputValue);

    setSelectOptions((prev) => [newOption, ...prev]);
    setValue(props.name, getValue(newOption));
  };

  if (props.control) {
    return (
      <CreatableSelectWithControllerView
        customStyles={customStyles}
        mapDataToFilters={mapDataToFilters}
        onChange={onChange}
        {...props}
        options={selectOptions}
        getValue={getValue}
        getLabel={getLabel}
        onCreateOption={handleCreate}
        formatCreateLabel={formatCreateLabel}
      />
    );
  }

  return (
    <CreatableSelectView
      customStyles={customStyles}
      mapDataToFilters={mapDataToFilters}
      onChange={onChange}
      {...props}
      options={selectOptions}
      getValue={getValue}
      getLabel={getLabel}
      onCreateOption={handleCreate}
      formatCreateLabel={formatCreateLabel}
    />
  );
};

export default CreatableSelect;
